import React, { useMemo } from 'react';

import { Button, Flex, Switch, Text } from 'src/client/components';
import {
  RECIPIENT_TYPE_DISPLAY,
  RECIPIENT_TYPE_ORDER,
} from 'src/client/constants/pageRecipients';
import {
  PageRecipient,
  RECIPIENT_TYPE,
  ValidRecipientType,
} from 'src/commons/types';

import * as S from './styles';

type Props = {
  unfilteredUnfeaturedPageRecipients: PageRecipient[];
  tempVisibleTypes: Set<RECIPIENT_TYPE>;
  onToggle: (type: RECIPIENT_TYPE, isVisible: boolean) => void;
  handleCancelEditingVisibleTypes: () => void;
  handleSaveVisibleTypes: () => void;
  isSaveDisabled: boolean;
  isOpen: boolean;
  onCancel: () => void;
};

function RecipientTypesFilterModal(props: Props) {
  const {
    isOpen,
    onCancel,
    unfilteredUnfeaturedPageRecipients,
    tempVisibleTypes,
    onToggle,
    handleCancelEditingVisibleTypes,
    handleSaveVisibleTypes,
    isSaveDisabled,
  } = props;

  return (
    <S.StyledModal
      footer={false}
      open={isOpen}
      zIndex={10000}
      onCancel={onCancel}
    >
      <S.ModalHeading>
        <Text type="h4med2">Show/Hide Recipient Types</Text>
        <Text type="body2reg2">Filter recipients displayed by type</Text>
      </S.ModalHeading>

      <S.ModalTip>
        <Text>📌</Text>
        <Text>
          To hide specific recipients, hover/click on the recipient and select
          “Hide”
        </Text>
      </S.ModalTip>

      <S.RecipientFilterSection>
        <RecipientTypeToggle
          unfeaturedPageRecipients={unfilteredUnfeaturedPageRecipients}
          visibleTypes={tempVisibleTypes}
          onToggle={onToggle}
        />
      </S.RecipientFilterSection>

      <Flex gap="8px" justifyContent="flex-end">
        <Button
          data-cy="cancel-save-adjective-btn"
          type="secondary"
          onClick={handleCancelEditingVisibleTypes}
        >
          Cancel
        </Button>
        <Button
          disabled={isSaveDisabled}
          type="primary"
          onClick={handleSaveVisibleTypes}
        >
          Save
        </Button>
      </Flex>
    </S.StyledModal>
  );
}

type RecipientTypeToggleProps = {
  onToggle: (type: RECIPIENT_TYPE, isVisible: boolean) => void;
  visibleTypes: Set<RECIPIENT_TYPE>;
  unfeaturedPageRecipients: PageRecipient[];
};

function shouldIncludeRecipientInType(
  recipientTypes: ValidRecipientType[] | undefined,
  typeToCheck: RECIPIENT_TYPE
): boolean {
  if (!recipientTypes) {
    return false;
  }

  if (typeToCheck === RECIPIENT_TYPE.OTHER) {
    return recipientTypes.some(
      (type) => type === RECIPIENT_TYPE.OTHER || type === 'political_campaign'
    );
  }

  return recipientTypes.includes(typeToCheck);
}

function RecipientTypeToggle(props: RecipientTypeToggleProps) {
  const { onToggle, visibleTypes, unfeaturedPageRecipients } = props;

  const getCountByType = (type: RECIPIENT_TYPE) =>
    unfeaturedPageRecipients.filter((recipient) =>
      shouldIncludeRecipientInType(recipient.recipientTypes, type)
    ).length;

  const sortedRecipientTypes = useMemo(
    () =>
      Object.values(RECIPIENT_TYPE).sort(
        (a, b) =>
          RECIPIENT_TYPE_ORDER.indexOf(a) - RECIPIENT_TYPE_ORDER.indexOf(b)
      ),
    []
  );

  return (
    <div>
      {sortedRecipientTypes.map((type) => {
        const count = getCountByType(type);
        const isVisible = visibleTypes.has(type);

        return (
          <S.RecipientTypeRow key={type}>
            <span>
              {RECIPIENT_TYPE_DISPLAY[type]} ({count})
            </span>
            <S.RecipientToggle>
              <span>{isVisible ? 'Show' : 'Hide'}</span>
              <Switch
                checked={isVisible}
                onChange={(checked) => onToggle(type, checked)}
              />
            </S.RecipientToggle>
          </S.RecipientTypeRow>
        );
      })}
    </div>
  );
}

export default RecipientTypesFilterModal;
